import * as React from 'react'
import styled from 'styled-components'
import { SPACE } from '../../styles/constants'
import { calcFontScale } from '../../styles/calcFontScale'
import { lighten } from 'polished'
import { withProps } from '../../styles/themed-components'
// @ts-ignore
import * as theme from 'styled-theming'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string
  placeholder?: string
  type?: string
  error?: string
  title?: string
  label?: string
  required?: boolean
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void
}

export const Input: React.SFC<InputProps> = ({
  name,
  type = 'text',
  label,
  placeholder,
  error,
  title,
  required,
  onChange,
  children,
}) => (
  <Wrapper>
    <Inner title={title}>
      <InputField
        type={type}
        name={name}
        placeholder={placeholder}
        error={error}
        required={required}
        onChange={onChange}
      />
      {label && <Label error={error}>{label}</Label>}
      {children}
      <InputDivider error={error} />
    </Inner>
    {error && <Error>{error}</Error>}
  </Wrapper>
)

const colors = {
  default: theme('mode', {
    light: (props: any) => lighten(0.75, props.theme.color.black),
    dark: (props: any) => lighten(0.875, props.theme.color.black),
  }),
  label: theme('mode', {
    light: (props: any) => lighten(0.5, props.theme.color.black),
    dark: (props: any) => lighten(0.75, props.theme.color.black),
  }),
  fieldText: theme('mode', {
    light: (props: any) => props.theme.color.black,
    dark: (props: any) => lighten(0.875, props.theme.color.black),
  }),
  focused: theme('mode', {
    light: (props: any) => lighten(0.125, props.theme.color.primary),
    dark: (props: any) => lighten(0.125, props.theme.color.primary),
  }),
  error: theme('mode', {
    light: (props: any) => props.theme.color.danger,
    dark: (props: any) => props.theme.color.danger,
  }),
}

type StyledProps = {
  error?: string
}

const Wrapper = styled.div`
  ${SPACE.stack.s};
`
const Inner = styled.label`
  display: block;
  position: relative;
`
const Label = withProps<StyledProps>()(styled.span)`
	color: ${(props: StyledProps) => (props.error ? colors.error : colors.label)};
  display: block;
  font-weight: 600
  left: 0;
  position: absolute;
  top: 0;
  transition: color 150ms ease-in-out;
`
const InputDivider = withProps<StyledProps>()(styled.div)`
	background-color: ${(props: StyledProps) => (props.error ? colors.error : colors.default)};
  bottom: 0;
  height: 1px;
  left: 0;
  position: absolute;
  width: 100%;
  &:before {
    content: '';
		background-color: ${(props: StyledProps) => (props.error ? colors.error : colors.focused)};
    display: block;
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    transition: width 200ms ease-in-out, opacity 100ms ease-in-out;
    width: 0%;
  }
`
const InputField = styled.input`
  appearance: none;
  background-color: transparent;
  border: 0;
  box-sizing: border-box;
  caret-color: ${colors.focused};
  color: ${(props: InputProps) => (props.error ? colors.error : colors.fieldText)};
  font-weight: bold;
  margin-top: 24px;
  padding-top: 8px;
  padding-bottom: 16px;
  width: 100%;
  ${calcFontScale(0)};

  &:focus {
    outline: none;
    & ~ ${InputDivider} {
      &:before {
        opacity: 1;
        width: 100%;
      }
    }
    & ~ ${Label} {
      color: ${colors.focused};
    }
    &::placeholder {
      opacity: 0;
    }
  }

  &::placeholder {
    color: ${(props: InputProps) => (props.error ? colors.error : colors.default)};
    transition: opacity 350ms cubic-bezier(0.23, 1, 0.32, 1);
  }
  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px white inset;
  }
`
const Error = styled.span`
  color: ${colors.error};
`
InputField.displayName = 'InputField'
Error.displayName = 'InputError'



// WEBPACK FOOTER //
// ./src/components/Input/index.tsx